import { useState, useEffect, createContext } from "react";
import { jwtDecode } from "jwt-decode";
import { User, Auth, Integration } from "../api";
import { hasExpiredToken } from "../utils";

const userController = new User();
const authController = new Auth();
const integrationController = new Integration();

export const AuthContext = createContext();

// Pendiente de volver a la otra app apenas haya sesión acá (ver #sso-to).
const SSO_RETURN_KEY = "ssoReturnToPeer";

// Lee lo que deja la otra app Sustemia en la URL y lo borra de la barra de
// direcciones:
//   #sso=<token>  viene logueado desde la otra app → canjear el token.
//   #sso-to       el usuario tocó "Ingresar con ..." en el login de la otra
//                 app → al tener sesión acá, mandarlo de vuelta logueado.
function takeSsoFromUrl() {
  const { hash } = window.location;
  if (!hash.startsWith("#sso")) return null;
  window.history.replaceState(null, "", window.location.pathname + window.location.search);
  const match = hash.match(/^#sso=(.+)$/);
  if (match) return decodeURIComponent(match[1]);
  if (hash === "#sso-to") sessionStorage.setItem(SSO_RETURN_KEY, "1");
  return null;
}

// Email de la cuenta que trae el token de traspaso (solo para mostrarlo; la
// validación real la hace el servidor).
function emailFromSsoToken(token) {
  try {
    const part = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(part)).email || null;
  } catch (error) {
    return null;
  }
}

// Antes de entrar con un link de la otra app, el usuario confirma la cuenta:
// evita que alguien lo haga trabajar en una cuenta ajena mandándole su link.
function confirmSsoAccount(token) {
  const email = emailFromSsoToken(token);
  return Boolean(email) && window.confirm(`¿Ingresar como ${email}?`);
}

export function AuthProvider(props) {
  const { children } = props;
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [laoding, setLaoding] = useState(true);
  const [ssoError, setSsoError] = useState(null);
  // Si al llegar desde la otra app acá se exige 2FA, el login muestra
  // directamente el formulario del código.
  const [ssoChallenge, setSsoChallenge] = useState(null);

  useEffect(() => {
    (async () => {
      const ssoToken = takeSsoFromUrl();
      if (ssoToken && confirmSsoAccount(ssoToken)) {
        // La sesión que trae la otra app reemplaza a cualquiera que hubiera acá.
        authController.removeTokens();
        try {
          const result = await integrationController.exchange(ssoToken);
          if (result.requires2FA) {
            setSsoChallenge(result.challengeToken);
          } else {
            authController.setAccessToken(result.access);
            authController.setRefreshToken(result.refresh);
          }
        } catch (error) {
          setSsoError(error.msg || "No se pudo ingresar desde la otra aplicación");
        }
      }

      const accessToken = authController.getAccessToken();
      const refreshToken = authController.getRefreshToken();

      if (!accessToken || !refreshToken) {
        logout();
        setLaoding(false);
        return;
      }

      if (hasExpiredToken(accessToken)) {
        if (hasExpiredToken(refreshToken)) {
          logout();
        } else {
          await reLogin(refreshToken);
        }
      } else {
        await login(accessToken);
      }

      setLaoding(false);
    })();
  }, []);

  const reLogin = async (refreshToken) => {
    try {
      // Preserva si la sesión se guardó para persistir (localStorage) o
      // solo para esta pestaña (sessionStorage) al re-emitir el access token.
      const remember = authController.isRemembered();
      const { accessToken } = await authController.refreshAccessToken(
        refreshToken
      );
      authController.setAccessToken(accessToken, remember);
      await login(accessToken);
    } catch (error) {
      console.error(error);
    }
  };

  const login = async (accessToken) => {
    try {
      const response = await userController.getMe(accessToken);
      delete response.password;

      // El sitio activo de la sesión viaja en el propio token (se preserva a
      // través de refresh/switch-site). Para admins sin restricción de sitio
      // el token no lleva 'site' y se preserva la selección guardada.
      const { site: activeSite } = jwtDecode(accessToken);
      if (activeSite) localStorage.setItem("site", activeSite);
      if (response.company) localStorage.setItem("company", response.company);

      setUser(response);
      // Guardamos el ID del sitio en localStorage

      setToken(accessToken);
    } catch (error) {
      console.error(error);
    }
  };

  // "Ingresar con ..." desde el login de la otra app: apenas hay sesión acá
  // (ya existente o recién iniciada), volver allá logueado.
  useEffect(() => {
    if (!token || !sessionStorage.getItem(SSO_RETURN_KEY)) return;
    sessionStorage.removeItem(SSO_RETURN_KEY);
    integrationController
      .handoff(token)
      .then(({ url }) => window.location.assign(url))
      .catch((error) => window.alert(error.msg || "No se pudo volver a la otra aplicación"));
  }, [token]);

  const logout = () => {
    setUser(null);
    setToken(null);
    authController.removeTokens();
  };

  const data = {
    accessToken: token,
    user,
    login,
    logout,
    ssoError,
    ssoChallenge,
  };

  if (laoding) return null;

  return <AuthContext.Provider value={data}>{children}</AuthContext.Provider>;
}
